import { useToggle } from "client/hooks/utils/useToggle";
import { BottomModal } from "components/common/BottomModal/BottomModal";
import { CloseIcon } from "components/common/Icons/CloseIcon";
import { ColorPickerRainBowIcon } from "components/common/Icons/ColorPickerRainbowIcon";
import { Mode } from "components/modules/Editors/MemeGenerator/ControlBar/TextSettings/Color/Mode/Mode";
import { useState } from "react";
import { Option } from "../Option/Option";
import type { Mode as ModeType } from "components/modules/Editors/MemeGenerator/ControlBar/TextSettings/Color/types";
import { ColorList } from "./ColorList/ColorList";
import type { TextBox } from "store/features/memeGenerator/types";
import { useOutline } from "components/modules/Editors/MemeGenerator/ControlBar/TextSettings/MoreOptions/MoreOptionsContent/Outline/useOutline";
import { Slider } from "components/common/Slider/Slider";
import { MAX_OUTLINE_SIZE, MAX_SHADOW_SIZE } from "store/features/memeGenerator/data/configs";
import { useShadow } from "components/modules/Editors/MemeGenerator/ControlBar/TextSettings/MoreOptions/MoreOptionsContent/Shadow/useShadow";
import { roundFloat } from "client/utils/roundFloat";
import { capitalize } from "client/utils/capitalize";
import { translateFunc } from "client/utils/translateFunc";
import { Translate } from "components/common/Translate/Translate";

interface ColorProps {
    textBox: TextBox;
}

export const Color = ({ textBox }: ColorProps) => {
    const { isOpen, onClose: close, open } = useToggle();
    const [mode, setMode] = useState<ModeType>("text");
    const { handleOutlineChange } = useOutline(textBox);
    const { handleShadowBlurRadiusChange } = useShadow(textBox);
    return (
        <>
            <Option icon={<ColorPickerRainBowIcon />} text={capitalize(translateFunc("color"))} onClick={open} />
            <BottomModal
                isOpen={isOpen}
                onClose={close}
                initialHeight={"330px"}
                containerId={"textbox-ignore"}
                titleBar={<TitleBar close={close} />}
            >
                <div className="flex flex-col gap-4 text-black dark:text-white">
                    <Mode textBox={textBox} mode={mode} setMode={setMode} />
                    <div className="flex flex-col gap-2 ml-4 mt-2">
                        <div className="font-semibold text-sm text-black dark:text-white">
                            <Translate keyName="select-a-text-color" />
                        </div>
                        <ColorList textBox={textBox} mode={mode} />
                    </div>
                    {mode === "outline" && textBox.outline && (
                        <div className="flex flex-col gap-4 px-4 pt-2 pb-4 border-t border-t-[#DCDDDF] dark:border-t-darkSurface-600">
                            <label className="text-sm font-semibold">
                                <Translate keyName="outline-width" />
                            </label>
                            <div className="flex items-center justify-between border border-irGray-200 dark:border-darkSurface-600 p-4 rounded-[10px] gap-4">
                                <div className="w-full">
                                    <Slider
                                        defaultValue={textBox.outline.size}
                                        handleChange={handleOutlineChange}
                                        max={MAX_OUTLINE_SIZE}
                                        min={0}
                                    />
                                </div>
                                <div className="translate-y-0.5">{textBox.outline.size}px</div>
                            </div>
                        </div>
                    )}
                    {mode === "shadow" && textBox.shadow && (
                        <div className="flex flex-col gap-4 px-4 pt-2 pb-4 border-t border-t-[#DCDDDF] dark:border-t-darkSurface-600">
                            <label className="text-sm font-semibold">
                                <Translate keyName="shadow-width" />
                            </label>
                            <div className="flex items-center justify-between border border-irGray-200 dark:border-darkSurface-600 p-4 rounded-[10px] gap-4">
                                <div className="w-full">
                                    <Slider
                                        defaultValue={textBox.shadow.blurRadius}
                                        handleChange={handleShadowBlurRadiusChange}
                                        max={MAX_SHADOW_SIZE}
                                        min={0}
                                    />
                                </div>
                                <div className="translate-y-0.5">{roundFloat(textBox.shadow.blurRadius)}px</div>
                            </div>
                        </div>
                    )}
                </div>
            </BottomModal>
        </>
    );
};

interface TitleBarProps {
    close: () => void;
}

const TitleBar = ({ close }: TitleBarProps) => (
    <div className="w-full py-2 flex items-center justify-center relative text-black dark:text-white">
        <div className="text-lg">
            <Translate keyName="select-a-text-color" />
        </div>
        <div className="absolute right-4 top-0 bottom-0 flex items-center justify-center">
            <button
                className="w-6 h-6 bg-irGray-300 dark:bg-darkSurface-200 rounded-full grid place-items-center"
                onClick={close}
            >
                <CloseIcon />
            </button>
        </div>
    </div>
);
